'use client';

import { useTranslations, useLocale } from 'next-intl';
import type React from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';

import { useDomainConfiguration } from '@/components/DomainConfigurationProvider';

import {
    DomainMovieConfiguration,
    HiddenSections,
    InitiallyExpandedSection,
    MovieBannerPlacement
} from '@cineamo/api/models/premium-movie-landing-page-configuration/PremiumMovieLandingPageConfigurationDto';

import { VIMEO_THUMBNAIL_IMAGE_DEFAULT, VIMEO_VIDEO_URL } from '@cineamo/api/constants/vimeo-constants';
import { YOUTUBE_EMBED_VIDEO_URL, YOUTUBE_THUMBNAIL_IMAGE_MAX_RES } from '@cineamo/api/constants/youtube-constants';

import {
    getMovieAccessibility,
    getMovieBackdropImages,
    hasMovieAccessibilityFeatures
} from '@cineamo/api/helpers/movie-helper';

import { getCountryCodeFromString, getLanguageCodeFromString } from '@cineamo/ui/helpers/locale-helper';

import ScrollToAnchor from '@cineamo/ui/src/misc/ScrollToAnchor';

import BackToTopButton from '@cineamo/composables/src/BackToTopButton';
import BannerDownloadApp from '@cineamo/composables/src/banners/BannerDownloadApp';
import CenterLayout from '@cineamo/composables/src/CenterLayout';
import CollapsibleSection from '@cineamo/composables/src/movie/CollapsibleSection';
import MovieImageGallery from '@cineamo/composables/src/movie/MovieImageGallery';
import MoviePartnerBox, { MoviePartnerBoxDirection } from '@cineamo/composables/src/movie/MoviePartnerBox';
import MovieTrailerPlayer from '@cineamo/composables/src/movie/MovieTrailerPlayer';
import { useAnalytics } from '@cineamo/composables/src/providers/analyticsProvider';
import { getLocationAction } from '@cineamo/composables/src/showtimes-finder/actions';
import GetLocationStoreData from '@cineamo/composables/src/stores/location/LocationStoreInitializer';

import type { TourStop } from '../page';

import CinemaTourSection from './CinemaTourSection';
import CompetitionSection from './CompetitionSection';
import CustomBannerSlot from './CustomBannerSlot';
import GretaAdSection from './GretaAdSection';
import HomeEntertainmentSection from './home-entertainment/HomeEntertainmentSection';
import MovieCrew from './MovieCrew';
import MovieHeader from './MovieHeader';
import MovieInfo from './MovieInfo';
import MovieTeachingMaterials from './MovieTeachingMaterials';
import PageHero from './PageHero';
import PartnerLogosSection from './PartnerLogosSection';
import PressQuotesSection from './PressQuotesSection';
import SectionHeader from './SectionHeader';
import WatchMoreSection from './WatchMoreSection';

type MovieLandingPageProps = {
    tourStops: TourStop[];
    domainConfiguration: DomainMovieConfiguration;
};

const MovieLandingPage: React.FC<MovieLandingPageProps> = ({ tourStops, domainConfiguration }) => {
    const t = useTranslations();
    // const format = useFormat();
    const { movie, movieDto } = useDomainConfiguration();
    const { track } = useAnalytics();
    // const { style } = domainConfiguration;
    const locale = useLocale() as 'en' | 'de';

    const [playTrailer, setPlayTrailer] = useState(false);
    const trailerSectionRef = useRef<HTMLDivElement>(null);
    const plotSectionRef = useRef<HTMLDivElement>(null);
    const infoSectionRef = useRef<HTMLDivElement>(null);

    const onWatchTrailerClick = useCallback(() => {
        trailerSectionRef?.current?.scrollIntoView({
            block: 'start',
            behavior: 'smooth'
        });
        setTimeout(() => setPlayTrailer(true), 1500);
    }, [setPlayTrailer]);

    const onPlotClick = useCallback(() => {
        plotSectionRef?.current?.scrollIntoView({
            block: 'center',
            behavior: 'smooth'
        });
    }, []);

    const onInfoClick = useCallback(() => {
        infoSectionRef?.current?.scrollIntoView({
            block: 'center',
            behavior: 'smooth'
        });
    }, []);

    const movieBackdrops: string[] = useMemo(() => {
        const imageGallery = domainConfiguration.imageGallery ?? [];
        if (imageGallery.length > 0) {
            return imageGallery;
        }
        const language = getLanguageCodeFromString(locale);
        const countryCode = getCountryCodeFromString(locale);
        return (
            getMovieBackdropImages(movieDto, {
                language: language,
                countryCode: countryCode,
                usePosterAsFallback: false,
                imageOptions: {
                    useTmdbUrlPrefix: false,
                    includeImagesWithoutLocale: true
                }
            }) || []
        );
    }, [movieDto, locale, domainConfiguration.imageGallery]);

    // `trailer` is stored either as a single legacy video or per locale; both shapes are read.
    const configuredTrailer = domainConfiguration.trailer;
    const trailer =
        (configuredTrailer && !('key' in configuredTrailer) ? configuredTrailer[locale] : undefined) ??
        (configuredTrailer && 'key' in configuredTrailer ? configuredTrailer : null) ??
        null;

    // A gretaMovieId alone doesn't guarantee real accessibility content in the GRETA app —
    // require at least one accessibility feature (same condition as cinema-website's ContentTitle)
    const movieHasGretaAccessibility = hasMovieAccessibilityFeatures(getMovieAccessibility(movieDto));

    return (
        <main className="min-h-screen max-w-screen overflow-x-hidden">
            <ScrollToAnchor />
            <GetLocationStoreData locationAction={getLocationAction} />
            <PageHero
                onWatchTrailerClick={onWatchTrailerClick}
                onPlotClick={onPlotClick}
                onInfoClick={onInfoClick}
            />

            <CenterLayout className="flex flex-col space-y-[56px] lg:space-y-[80px] lg:pt-48 pb-[56px] lg:pb-[96px]">
                <CustomBannerSlot placement={MovieBannerPlacement.BELOW_HERO} />

                <MovieHeader ref={plotSectionRef} />

                {/* Trailer Section */}
                {trailer && !domainConfiguration.hiddenSections?.includes(HiddenSections.TRAILER) && (
                    <section ref={trailerSectionRef}>
                        <SectionHeader title={t('movie.trailer')} />
                        <MovieTrailerPlayer
                            videoUrl={
                                trailer.site === 'YouTube'
                                    ? YOUTUBE_EMBED_VIDEO_URL(trailer.key)
                                    : VIMEO_VIDEO_URL(trailer.key)
                            }
                            thumbnailUrl={
                                trailer.site === 'YouTube'
                                    ? YOUTUBE_THUMBNAIL_IMAGE_MAX_RES(trailer.key)
                                    : VIMEO_THUMBNAIL_IMAGE_DEFAULT(trailer.key)
                            }
                            movieTitle={movie.title}
                            isPlaying={playTrailer}
                            onPlay={() => {
                                setPlayTrailer(true);
                                track('Movie Trailer Played');
                            }}
                            onPause={() => {
                                // Intentionally don't set playTrailer to false here
                                // Because of the bug in react-player library where clicking on seek bar will trigger onPause event
                            }}
                            onEnded={() => setPlayTrailer(false)}
                        />
                    </section>
                )}

                <CustomBannerSlot placement={MovieBannerPlacement.AFTER_TRAILER} />

                {/* Watch More Section (Placeholder) */}
                {!domainConfiguration.hiddenSections?.includes(HiddenSections.WATCH_MORE) && (
                    <section>
                        <WatchMoreSection />
                    </section>
                )}

                <CustomBannerSlot placement={MovieBannerPlacement.AFTER_WATCH_MORE} />

                {/* Image Gallery Section */}
                {movieBackdrops.length > 0 &&
                    !domainConfiguration.hiddenSections?.includes(HiddenSections.IMAGES_GALLERY) && (
                        <section>
                            <MovieImageGallery
                                label={<SectionHeader title={t('movie.images-gallery')} />}
                                images={movieBackdrops}
                            />
                        </section>
                    )}

                <CustomBannerSlot placement={MovieBannerPlacement.AFTER_GALLERY} />

                {/* GRETA Ad Section — only for movies with accessibility content in the GRETA app */}
                {movieHasGretaAccessibility && !domainConfiguration.hiddenSections?.includes(HiddenSections.GRETA) && (
                    <GretaAdSection />
                )}

                <CustomBannerSlot placement={MovieBannerPlacement.AFTER_GRETA} />

                {/* Home Entertainment Section — jump target of the hero CTA once the movie left the cinemas */}
                <HomeEntertainmentSection />

                {/* Press Quotes Section - Full width background */}
                <PressQuotesSection quotes={domainConfiguration.pressQuotes?.[locale] ?? []} />

                <CustomBannerSlot placement={MovieBannerPlacement.AFTER_PRESS_QUOTES} />

                {/* Cinema Tour Section */}
                {tourStops.length > 0 && <CinemaTourSection stops={tourStops} />}

                <CustomBannerSlot placement={MovieBannerPlacement.AFTER_TOUR} />

                {/* Competition Section */}
                <CompetitionSection />

                <CustomBannerSlot placement={MovieBannerPlacement.AFTER_COMPETITION} />

                {/* Collapsible Sections (Example) */}
                <section
                    className="divide-y-1 divide-white-20 border-b-1 border-t-1 border-white-20"
                    ref={infoSectionRef}>
                    <CollapsibleSection
                        title={t('movie.cast-and-crew')}
                        initiallyExpanded={
                            domainConfiguration.initiallyExpandedSection === InitiallyExpandedSection.CAST_AND_CREW
                        }>
                        <MovieCrew />
                    </CollapsibleSection>
                    {!domainConfiguration.hiddenSections?.includes(HiddenSections.COLLAPSIBLE_INFO) && (
                        <CollapsibleSection
                            title={t('movie.info')}
                            initiallyExpanded={
                                domainConfiguration.initiallyExpandedSection === InitiallyExpandedSection.INFO
                            }>
                            <MovieInfo />
                        </CollapsibleSection>
                    )}
                    {domainConfiguration.teachingMaterials?.[locale]?.materials?.length && (
                        <CollapsibleSection
                            title={
                                domainConfiguration.teachingMaterials?.[locale]?.headline ||
                                t('movie.teaching-materials')
                            }
                            initiallyExpanded={
                                domainConfiguration.initiallyExpandedSection ===
                                InitiallyExpandedSection.TEACHING_MATERIALS
                            }>
                            <MovieTeachingMaterials />
                        </CollapsibleSection>
                    )}
                </section>

                <CustomBannerSlot placement={MovieBannerPlacement.AFTER_COLLAPSIBLE} />

                {/* Partner Logos Section */}
                <PartnerLogosSection />

                {domainConfiguration.cineamoBanner && (
                    <BannerDownloadApp
                        id="movie_landingpage"
                        isClosable
                        customImage={{
                            desktop: domainConfiguration.cineamoBanner?.desktopUrl,
                            mobile: domainConfiguration.cineamoBanner?.mobileUrl
                        }}
                    />
                )}

                <CustomBannerSlot placement={MovieBannerPlacement.ABOVE_FOOTER} />

                <section className="mx-auto my-24">
                    <MoviePartnerBox
                        movieImdbId={movieDto.imdbId}
                        movieThirdParties={movieDto.thirdParties}
                        movieSeries={movieDto.series}
                        movieTmdbId={movieDto.tmdbId}
                        distributor={domainConfiguration.distributor ?? undefined}
                        desktopOrientation={MoviePartnerBoxDirection.Horizontal}
                        movieSlug={movieDto.slug}
                        aboutMovie={'Cineamo'}
                    />
                </section>
                <div
                    style={
                        {
                            '--primary-color':
                                domainConfiguration.backToTopLinkColor === 'secondary'
                                    ? domainConfiguration.style.secondaryColor
                                    : domainConfiguration.style.primaryColor
                        } as React.CSSProperties
                    }>
                    <BackToTopButton />
                </div>
            </CenterLayout>
        </main>
    );
};

export default MovieLandingPage;
